import {Button, CircularProgress, FormLabel, IconButton, Stack} from "@mui/material";
import {FILE_TYPES} from "../fileConstants";
import {DragDropContext, Draggable, DraggingStyle, Droppable, DropResult, NotDraggingStyle} from "react-beautiful-dnd";
import React, {Ref, useRef} from "react";
import {CommonProps} from "./types/types";
import {AppAttachment} from "../types/types";
import {useTranslation} from "i18nano";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import {StatusUpdateAnswer} from "../types/UpdatePartyObject";
import DisplayTextComponent from "./DisplayTextComponent";
import PopupConfirm from "./PopupConfirm";
import {isMobileOrTablet} from "../helpers/deviceHelper";

const grid = 2;
const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle | undefined
): React.CSSProperties | undefined =>
        ({
            userSelect: "none",
            padding: grid * 2,
            margin: `0 0 ${grid}px 0`,
            background: isDragging ? "lightgreen" : "none",
            width: "100%",
            cursor: isDragging ? "grab" : "pointer",
            ...draggableStyle
        });

const getListStyle = () => ({
    padding: grid,
    width: '100%'
});


interface FileComponentProps extends CommonProps {
    attachments: AppAttachment[]
    onFilesUpload: (files: FileList | null) => void;
    onReorder: (attachments: AppAttachment[]) => void;
    onClickDelete: (attachment: AppAttachment) => void;
    onFileClick: (attachment: AppAttachment) => void;
}

const FileComponent = ({
                           editable, displayText, errorText, status, required,
                           attachments, onFilesUpload, onReorder, onClickDelete, onFileClick, id,
                           hint
                       }: FileComponentProps) => {
    const idLabelUpload = id;
    const t = useTranslation();

    const [statusFile, setStatusFile] = React.useState<"none" | "drag" | "focus">("none");

    const dragLeaveFileHandler = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setStatusFile("none");
    }
    const dragStartFileHandler = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setStatusFile("drag");
    }
    const ref = useRef<Ref<HTMLSpanElement>>(null);
    const dropFileHandler = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        let files: FileList | null;
        if ("dataTransfer" in e) {
            files = e.dataTransfer.files;
            setStatusFile("none");
            onFilesUpload(files);
        }
    }
    const changeFileHandler = (e: React.FormEvent<HTMLLabelElement>) => {
        e.preventDefault();
        const input = e.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            setStatusFile("none")
            onFilesUpload(input.files);
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const newAttachments = [...attachments];
        const [removed] = newAttachments.splice(result.source.index, 1);
        newAttachments.splice(result.destination.index, 0, removed);

        const updatedOrder = newAttachments.map((item, index) => ({
            ...item,
            order: index
        }));

        onReorder(updatedOrder);
    }

    return <>
        <FormLabel error={!!errorText} disabled={!editable} required={false}>
            <DisplayTextComponent text={displayText} required={required} hint={hint}/>
        </FormLabel>
        <label
                style={{marginTop: 0}}
                onDragStart={e => {
                    if (status === StatusUpdateAnswer.UPDATING) return;
                    dragStartFileHandler(e)
                }}
                onDragLeave={e => {
                    if (status === StatusUpdateAnswer.UPDATING) return;
                    dragLeaveFileHandler(e)
                }}
                onDragOver={e => {
                    if (status === StatusUpdateAnswer.UPDATING) return;
                    dragStartFileHandler(e)
                }}
                onDrop={dropFileHandler}
                htmlFor={idLabelUpload}
                onChange={changeFileHandler}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        const element = document.getElementById(idLabelUpload);
                        if (!element) return;
                        element.click();
                    }
                }}
                onBlur={() => {
                    if (statusFile === "focus") {
                        setStatusFile("none");
                    }
                }}


        >
            <input disabled={!editable || status === StatusUpdateAnswer.UPDATING}
                   style={{display: "none"}}
                   id={idLabelUpload} multiple type="file"
                   accept={FILE_TYPES.join(", ")}

            />

            <Button disabled={!editable || status === StatusUpdateAnswer.UPDATING} fullWidth
                    sx={{height: 150}} variant="outlined"
                    style={{textAlign: "center"}}
                    component="span"
                    color={statusFile === "drag" || statusFile === "focus" ? "success" : errorText ? "error" : "primary"}
                    onFocusVisible={() => {
                        setStatusFile("focus");
                    }}
                    ref={ref.current}
            >
                {
                    statusFile === "drag" ? t("Drop files") :
                            statusFile === "focus" ? t("Press enter for choose files") :
                                    status === StatusUpdateAnswer.UPDATING ? <CircularProgress/> :
                                            !!errorText ? t("This field is required") : t("Drag your files or click here")
                }
            </Button>
        </label>
        {
                attachments &&
                <Stack direction={"row"}
                       sx={{width: {md: "50%", sm: "100%"}, margin: "10px 0", flexWrap: "wrap"}}>
                    {
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="droppable" isDropDisabled={!editable}>
                                {(provided) => (
                                        <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                style={getListStyle()}
                                        >
                                            {attachments.map(
                                                    (file, index) => (
                                                            <Draggable key={file.id}
                                                                       disableInteractiveElementBlocking={!editable}
                                                                       isDragDisabled={!editable}
                                                                       draggableId={`${file.id}`} index={index}>
                                                                {(draggableProvided, snapshot) => {
                                                                    return (
                                                                            <div
                                                                                    ref={draggableProvided.innerRef}
                                                                                    {...draggableProvided.draggableProps}
                                                                                    {...draggableProvided.dragHandleProps}
                                                                                    style={getItemStyle(
                                                                                            snapshot.isDragging,
                                                                                            draggableProvided.draggableProps.style
                                                                                    )}
                                                                            >
                                                                                <div style={{
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "space-between"
                                                                                }}
                                                                                     className={isMobileOrTablet() ? "buttonDocumentDivMobile" : "buttonDocumentDiv"}>
                                                                                                <span
                                                                                                        onClick={() => onFileClick(file)}
                                                                                                        className={"buttonDocument"}>
                                                                                                    {`${index + 1}. ${file.displayName}`}
                                                                                                </span>
                                                                                    {
                                                                                            editable &&
                                                                                            <span>
                                                                                                        <PopupConfirm
                                                                                                                text={`${t("Do you really want to delete this file")}?`}
                                                                                                                onYes={() => onClickDelete(file)}
                                                                                                                yesText={t("Yes")}
                                                                                                                noText={t("No")}
                                                                                                        >
                                                                                                            <IconButton
                                                                                                                    size="small">
                                                                                                                <DeleteIcon
                                                                                                                        fontSize="small"/>
                                                                                                            </IconButton>
                                                                                                        </PopupConfirm>
                                                                                                    <DragIndicatorIcon
                                                                                                            fontSize="small"
                                                                                                            style={{marginRight: "5px"}}/>
                                                                                                </span>

                                                                                    }

                                                                                </div>
                                                                            </div>)
                                                                }
                                                                }
                                                            </Draggable>
                                                    ))}
                                            {provided.placeholder}
                                        </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    }
                </Stack>
        }
    </>
}

export default FileComponent;